import React, { Fragment, memo, useCallback, useContext, useMemo, useState } from 'react'
import { MenuActionItem } from 'src/interfaces/ui'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { CancelMinor, ReplayMinor, ViewMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { useDispatch, useSelector } from 'react-redux'
import {
  CancelPayment,
  DeletePayment,
  FetchPaymentInvoice,
  ProcessPayment,
  ProcessPaymentIgnoringGracePeriod,
  VoidPayment,
} from 'src/constants/actionTypes'
import { getPartnerId } from 'src/selectors/user'
import { showToast } from 'src/utils/toast'
import { PaymentStatus, SingleFailureReasonType } from 'src/constants/payment/enums'
import { Payment } from 'src/interfaces/payment'
import { Typography } from '@mui/material'
import { PaymentInvoiceModal } from '../../PaymentInvoiceModal'
import { UserPermission } from 'src/constants/user'
import { handleApiError } from 'src/utils/errorHandlers'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { PaymentsTableContext } from '../../payments-table/PaymentsTable.context'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { colors } from 'src/theme'
import { GSIcon } from 'src/components/shared/GSIcon'
import { VoidPaymentModal } from '../../VoidPaymentModal'

const PaymentsItemActionMenuComponent = () => {
  const dispatch = useDispatch()

  const { reload } = useContext(ReloadWrapperContext)
  const {
    paymentToAction,
    actionAnchor,
    closePaymentsItemAction,
    clearPaymentsItemAction,
    updatePayment,
  } = useContext(PaymentsTableContext)

  const partnerId = useSelector(getPartnerId)

  const { hasPermission } = useCheckPermissions()

  const {
    isOpen: isDeleteConfirmOpen,
    open: handleOpenDeleteConfirm,
    close: handleCloseDeleteConfirm,
  } = useModalOpen()

  const {
    isOpen: isCancelConfirmOpen,
    open: handleOpenCancelConfirm,
    close: handleCloseCancelConfirm,
  } = useModalOpen()

  const {
    isOpen: isVoidConfirmOpen,
    open: handleOpenVoidConfirm,
    close: handleCloseVoidConfirm,
  } = useModalOpen()

  const isOpen = Boolean(actionAnchor)

  const [invoice, setInvoice] = useState<any>(null)

  const clearInvoice = useCallback(() => {
    setInvoice(null)
  }, [])

  const handleDeletePayment = useCallback(async () => {
    if (!paymentToAction || !partnerId) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        DeletePayment.request({
          partnerId,
          paymentIds: [paymentToAction.id],
        }),
      )

      reload()

      showToast('Settlement is deleted.', { variant: 'success' })
    } catch (err) {
      handleApiError(err)
    }

    handleCloseDeleteConfirm()
  }, [paymentToAction, partnerId, reload])

  const handleCancelPayment = useCallback(async () => {
    if (!paymentToAction || !partnerId) {
      return
    }

    try {
      await createAsyncAction(
        dispatch,
        CancelPayment.request({
          paymentId: paymentToAction.id,
        }),
      )

      reload()

      showToast('Settlement is canceled.', { variant: 'success' })
    } catch (err) {
      handleApiError(err)
    }

    handleCloseCancelConfirm()
  }, [paymentToAction, partnerId, reload])

  const handleVoidPayment = useCallback(
    async (voidNote: string) => {
      if (!paymentToAction) {
        return
      }

      try {
        await createAsyncAction(
          dispatch,
          VoidPayment.request({
            paymentId: paymentToAction.id,
            voidNote,
          }),
        )

        reload()

        showToast('Settlement has been voided.', { variant: 'success' })
      } catch (err) {
        handleApiError(err)
      }

      handleCloseVoidConfirm()
    },
    [paymentToAction, dispatch, reload, handleCloseVoidConfirm],
  )

  const handleDownloadInvoice = useCallback(async () => {}, [invoice])

  const canIgnoreGracePeriod = useMemo(
    () =>
      paymentToAction?.status === PaymentStatus.Failed &&
      paymentToAction?.failureReasonType === SingleFailureReasonType.ContractorDeactivated &&
      hasPermission(UserPermission.PaymentsIgnoreGracePeriod),
    [paymentToAction?.status, paymentToAction?.failureReasonType, hasPermission],
  )

  const handleRetryIgnoringGracePeriod = useCallback(async () => {
    if (!paymentToAction) {
      return
    }

    try {
      const processedPayment: Payment = await createAsyncAction(
        dispatch,
        ProcessPaymentIgnoringGracePeriod.request({ paymentId: paymentToAction.id }),
      )

      updatePayment(processedPayment)

      showToast('Settlement is being processed', { variant: 'success' })
    } catch (error) {
      handleApiError(error)
    }
  }, [paymentToAction, updatePayment, dispatch])

  const handleRetryPayment = useCallback(async () => {
    if (!paymentToAction) {
      return
    }

    try {
      const processedPayment: Payment = await createAsyncAction(
        dispatch,
        ProcessPayment.request({ paymentId: paymentToAction.id }),
      )

      updatePayment(processedPayment)

      if (processedPayment.status === PaymentStatus.Failed) {
        throw processedPayment.failureReason
      }

      if (processedPayment.status === PaymentStatus.Pending) {
        showToast('Processing your settlement', { variant: 'warning' })

        return
      }

      showToast('Settlement has proceeded', { variant: 'success' })
    } catch {
      showToast(
        <Typography variant="body2">
          This settlement cannot be retried. Please email{' '}
          <a
            href="mailto:support@gigsafe.com"
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            support@gigsafe.com
          </a>{' '}
          for the settlement to be reuploaded and processed for you.
        </Typography>,
        {
          variant: 'error',
          autoHideDuration: 10000,
        },
      )
    }
  }, [paymentToAction, updatePayment, dispatch])

  const actionItems: MenuActionItem[] = useMemo(() => {
    const res: any = []

    if (paymentToAction?.status === PaymentStatus.Paid && paymentToAction.isInvoiceGenerated) {
      res.push({
        label: 'View invoice',
        icon: <ViewMinor width={20} height={20} />,
        dataTestId: `payments-main-row-${paymentToAction.id}-view-invoice`,
        onClick: async () => {
          if (!paymentToAction) {
            return
          }

          const buffer: any = await createAsyncAction(
            dispatch,
            FetchPaymentInvoice.request({
              partnerId,
              paymentId: paymentToAction.id,
            }),
          )

          const blob = new Blob([buffer], { type: 'application/pdf' })

          setInvoice(URL.createObjectURL(blob))

          closePaymentsItemAction()
        },
      })
    }

    if (
      paymentToAction?.status &&
      [PaymentStatus.NotPaid, PaymentStatus.RequiresFunding].includes(paymentToAction.status) &&
      hasPermission(UserPermission.PaymentsDelete)
    ) {
      res.push({
        label: 'Delete Settlement',
        icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
        color: colors.COLORS_RED_500,
        dataTestId: `payments-main-row-${paymentToAction.id}-delete`,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenDeleteConfirm()
          closePaymentsItemAction()
        },
      })
    }

    if (
      paymentToAction?.status === PaymentStatus.Failed &&
      paymentToAction?.isRetryable &&
      hasPermission(UserPermission.PaymentsProcess)
    ) {
      res.push({
        label: 'Retry settlement',
        icon: <ReplayMinor width={20} height={20} />,
        dataTestId: `payments-main-row-${paymentToAction.id}-retry`,
        onClick: () => {
          closePaymentsItemAction()
          handleRetryPayment()
        },
      })
    }

    if (paymentToAction && canIgnoreGracePeriod) {
      res.push({
        label: 'Ignore grace period',
        icon: <GSIcon iconName="gs_icon_replay" width={20} height={20} />,
        dataTestId: `payments-main-row-${paymentToAction.id}-ignore-grace-period`,
        onClick: () => {
          closePaymentsItemAction()
          handleRetryIgnoringGracePeriod()
        },
      })
    }

    if (paymentToAction?.isCancelable && hasPermission(UserPermission.PaymentsEdit)) {
      res.push({
        label: 'Cancel Settlement',
        icon: <CancelMinor width={20} height={20} fill={colors.COLORS_RED_500} />,
        color: colors.COLORS_RED_500,
        dataTestId: `payments-main-row-${paymentToAction.id}-cancel`,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenCancelConfirm()
          closePaymentsItemAction()
        },
      })
    }

    if (
      paymentToAction?.status === PaymentStatus.Failed &&
      hasPermission(UserPermission.PaymentsEdit)
    ) {
      res.push({
        label: 'Void Settlement',
        icon: (
          <GSIcon iconName="gs_circle_x" width={20} height={20} css={tw`[&_path]:stroke-black`} />
        ),
        dataTestId: `payments-main-row-${paymentToAction.id}-void`,
        onClick: () => {
          if (!paymentToAction) {
            return
          }

          handleOpenVoidConfirm()
          closePaymentsItemAction()
        },
      })
    }

    return res
  }, [
    paymentToAction,
    hasPermission,
    canIgnoreGracePeriod,
    handleOpenCancelConfirm,
    handleOpenDeleteConfirm,
    handleOpenVoidConfirm,
    handleRetryIgnoringGracePeriod,
    handleRetryPayment,
    closePaymentsItemAction,
    dispatch,
    partnerId,
  ])

  return (
    <Fragment>
      <ActionMenu
        anchorEl={actionAnchor}
        itemClassName={tw`min-w-[208px]`}
        isOpen={isOpen}
        items={actionItems}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={clearPaymentsItemAction}
      />
      <SensitiveActionModal
        isOpen={isDeleteConfirmOpen}
        title="Delete settlement"
        text="Are you sure you want to delete settlement?"
        submitLabel="Delete"
        onClose={handleCloseDeleteConfirm}
        onSubmit={handleDeletePayment}
      />
      <SensitiveActionModal
        isOpen={isCancelConfirmOpen}
        title="Cancel settlement"
        text="Are you sure you want to cancel settlement?"
        submitLabel="Cancel"
        onClose={handleCloseCancelConfirm}
        onSubmit={handleCancelPayment}
      />
      <PaymentInvoiceModal
        isOpen={Boolean(invoice)}
        invoice={invoice}
        onClose={clearInvoice}
        onDownload={handleDownloadInvoice}
      />
      <VoidPaymentModal
        isOpen={isVoidConfirmOpen}
        onClose={handleCloseVoidConfirm}
        onSubmit={handleVoidPayment}
      />
    </Fragment>
  )
}

export const PaymentsItemActionMenu = memo(PaymentsItemActionMenuComponent)
